import React from 'react';
import moment from 'moment';
import momentTz from 'moment-timezone';
import Bowser from 'bowser';
import IntlWrapper from './languages/IntlWrapper';
import Auth from './Auth';
import ThemeProvider from './themes/ThemeProvider';
import MainPage from './factory/main/containers/MainPage';
import LoginPage from './factory/login/containers/LoginPage';
import PasswordResetRequestPage from 'factory/login/containers/PasswordResetRequestPage';
import PasswordResetPage from 'factory/login/containers/PasswordResetPage';
import TwoAuthPage from 'factory/login/containers/TwoAuthPage';

import * as ReactDOM from 'react-dom/client';
import * as StackTrace from 'stacktrace-js';
import * as log4javascript from 'log4javascript';

import { ApolloClient } from '@apollo/client';
import { createHttpLink } from '@apollo/client';
import { InMemoryCache } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { ApolloProvider } from '@apollo/client/react';

import { BrowserRouter } from 'react-router-dom';
import { Route, Routes } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';

import { Provider } from 'react-redux';
import { displaySnackbar } from './factory/main/snackbarReducer';
import { checkVersion } from './factory/version/VersionChecker';
import { showNewVersionDialog } from './factory/version/versionCheckerReducer';
import { sessionExpired } from './factory/login/sessionReducer';
import { configureAppStore } from './configureStore';

import 'moment/locale/lt';
import 'es6-shim';

import './index.css';
import './styles/bootstrap.scss';
import './styles/bootstrap/bootstrap-grid.scss';
import './styles/bootstrap/bootstrap-reboot.scss';
import './styles/layout.scss';
import './styles/theme.scss';
import './styles/ui.scss';
import './styles/app.scss';
import './styles/loader.scss';

import $ from 'jquery';
import 'malihu-custom-scrollbar-plugin';
import 'malihu-custom-scrollbar-plugin/jquery.mCustomScrollbar.css';
import PrivacyPolicyPage from 'factory/privacyPolicy/PrivacyPolicyPage';

log4javascript.logLog.setQuietMode(true);
const logger = log4javascript.getLogger('Factory app');
logger.removeAllAppenders();

const ajaxAppender = new log4javascript.AjaxAppender(
    `${process.env.REACT_APP_API || ''}/logger`
);

ajaxAppender.setThreshold(log4javascript.Level.INFO);

logger.addAppender(ajaxAppender);

window.onerror = (
    message: string | Event,
    error: any
) => {
    displaySystemError();
    if (logger) {
        StackTrace.fromError(error)
            .then((stackframes: StackTrace.StackFrame[]) => {
                const stack = stackframes
                    .map((sf: StackTrace.StackFrame) => {
                        return sf.toString();
                    })
                    .join('\n');
                const browser = JSON.stringify(Bowser.parse(window.navigator.userAgent));
                const jwt = Auth.getJWT();
                const user = jwt ? jwt.id : undefined;
                logger.error(
                    `An error occured: ${message}\n Stack: ${stack}\n Browser: ${browser}\n` +
                    ` User: ${user}\n Version: ${process.env.REACT_APP_VERSION}`
                );
            })
            .catch((err: Error) => {
                // tslint:disable-next-line
                console.log(err.message);
            });
    }
};

logger.info('Logger have been configured in React app');

moment.locale('lt');
momentTz.locale('lt');
momentTz.tz.setDefault(momentTz.tz.guess());

const store = configureAppStore((() => {}) as any);

const getValidAuthToken = () => {
    const token = Auth.getToken();

    if (typeof token !== 'string') {
        return null;
    }

    const trimmedToken = token.trim();

    if (
        !trimmedToken ||
        trimmedToken === 'null' ||
        trimmedToken === 'undefined' ||
        trimmedToken.split('.').length !== 3
    ) {
        return null;
    }

    return trimmedToken;
};

const apollo = new ApolloClient({
    link: onError((errorResponse: any) => {
        const { graphQLErrors, networkError } = errorResponse;

        if (networkError) {
            const statusCode =
                (networkError as any)?.statusCode ??
                (networkError as any)?.response?.status;

            if (statusCode === 401) {
                store.dispatch((sessionExpired as any)());
            } else {
                logger.error(JSON.stringify(networkError));
                displaySnackbar('network.error.message', 'info', false, true)(
                    store.dispatch
                );
            }
        }
        if (graphQLErrors) {
            logger.info(JSON.stringify(graphQLErrors));
        }
    })
    .concat(
        setContext((_, { headers }) => {
            const token = getValidAuthToken();
    
            return {
                headers: {
                    ...headers,
                    ...(token ? { authorization: `Bearer ${token}` } : {}),
                },
            };
        })
    )
    .concat(
        createHttpLink({ uri: `${process.env.REACT_APP_API || ''}/api/graphql` })
    ),
    cache: new InMemoryCache(),
});

const displaySystemError = () => {
    displaySnackbar('system.error.message', 'danger', false, true)(
        store.dispatch
    );
};

const onVersionMismatch = () => {
    showNewVersionDialog()(store.dispatch);

    setTimeout(() => {
        window.location.reload();
    }, 60*60*1000);
};
checkVersion(onVersionMismatch);

const useLegacyRouteProps = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();

    const history = {
        push: (to: any, state?: any) => navigate(to, { state }),
        replace: (to: any, state?: any) => navigate(to, { replace: true, state }),
        goBack: () => navigate(-1)
    } as any;

    const match = { params } as any;

    return { history, location, match } as any;
};

const MainPageRoute = () => {
    const props = useLegacyRouteProps();
    return <MainPage {...props} />;
};

const LoginPageRoute = () => {
    const props = useLegacyRouteProps();

    const nextPath =
        props.location.pathname && props.location.pathname !== '/login'
            ? props.location.pathname
            : '/';

    return <LoginPage nextPath={nextPath} {...props} />;
};

const PasswordResetRequestPageRoute = () => {
    const props = useLegacyRouteProps();
    return <PasswordResetRequestPage {...props} />;
};

const PasswordResetPageRoute = () => {
    const props = useLegacyRouteProps();
    return <PasswordResetPage {...props} />;
};

const TwoAuthPageRoute = () => {
    const props = useLegacyRouteProps();

    const nextPath =
        props.location.pathname && props.location.pathname !== '/2fa'
            ? props.location.pathname
            : '/';

    return <TwoAuthPage nextPath={nextPath} {...props} />;
};

const RootRoute = () => {
    const props = useLegacyRouteProps();

    if (!Auth.isUserAuthenticated()) {
        return <LoginPage nextPath={props.location.pathname} {...props} />;
    }

    if (!Auth.is2FAComplete()) {
        return <TwoAuthPage nextPath={props.location.pathname} {...props} />;
    }

    return <MainPage {...props} />;
};

const container = document.getElementById('app-container') as HTMLElement;
const root = ReactDOM.createRoot(container);

root.render(
    <ThemeProvider>
        <Provider store={store}>
            <ApolloProvider client={apollo}>
                <IntlWrapper>
                    <BrowserRouter>
                        <Routes>
                            <Route path="/login" element={<LoginPageRoute />} />
                            <Route
                                path="/requestResetPassword"
                                element={<PasswordResetRequestPageRoute />}
                            />
                            <Route
                                path="/resetPassword/:userId/:token"
                                element={<PasswordResetPageRoute />}
                            />
                            <Route
                                path="/2fa"
                                element={<TwoAuthPageRoute />}
                            />
                            <Route 
                                path="/privacy-policy"
                                element={<PrivacyPolicyPage />} />
                            <Route
                                path="/*"
                                element={<RootRoute />}
                            />
                        </Routes>
                    </BrowserRouter>
                </IntlWrapper>
            </ApolloProvider>
        </Provider>
    </ThemeProvider>
);